import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Button,
  Stack,
  Chip,
  Grid,
  Paper,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  NotificationsActive,
  AssignmentTurnedIn,
  LocalShipping,
  Timer,
  CheckCircle,
} from "@mui/icons-material";
import api from "../../api/axios";
import socket from "../../socket";

const AgentOrders = () => {
  const navigate = useNavigate();
  const agent = JSON.parse(localStorage.getItem("user"));

  const [tabValue, setTabValue] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [acceptedOrders, setAcceptedOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [dialogAction, setDialogAction] = useState(null);

  // ⛔ Protect agent route
  useEffect(() => {
    if (!agent?._id) {
      navigate("/agent/login", { replace: true });
      return;
    }

    /* Join agent notification room */
    socket.emit("agent_join", agent._id);

    return () => {
      socket.emit("agent_leave", agent._id);
    };
  }, [agent, navigate]);

  // 🔔 Fetch pending notifications
  const fetchNotifications = useCallback(async () => {
    if (!agent?._id) return;
    try {
      setLoading(true);
      const { data } = await api.get("/agent/notifications/pending");
      setNotifications(data.notifications || []);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      setLoading(false);
    }
  }, [agent?._id]);

  // 📦 Fetch accepted orders
  const fetchAcceptedOrders = useCallback(async () => {
    if (!agent?._id) return;
    try {
      const { data } = await api.get("/agent/orders/accepted");
      setAcceptedOrders(data.orders || []);
    } catch (error) {
      console.error("Failed to fetch orders:", error);
    }
  }, [agent?._id]);

  // Initial fetch
  useEffect(() => {
    if (agent?._id) {
      fetchNotifications();
      fetchAcceptedOrders();
    }
  }, [agent?._id, fetchNotifications, fetchAcceptedOrders]);

  // 🔔 Listen for new notifications via socket
  useEffect(() => {
    socket.on("new_order_notification", (notification) => {
      console.log("🔔 New order notification received:", notification);
      fetchNotifications(); // Refresh notifications
    });

    socket.on("order_taken_notification", (data) => {
      console.log("📢 Order taken:", data);
      // Remove from notifications
      setNotifications((prev) =>
        prev.filter((n) => n.orderId !== data.orderId)
      );
    });

    return () => {
      socket.off("new_order_notification");
      socket.off("order_taken_notification");
    };
  }, [fetchNotifications]);

  // ✅ Accept order
  const handleAcceptOrder = async () => {
    if (!selectedNotification) return;

    try {
      setLoading(true);
      await api.post(
        `/agent/notifications/${selectedNotification.orderId}/accept`
      );

      // Remove from notifications and refresh
      setNotifications((prev) =>
        prev.filter((n) => n.orderId !== selectedNotification.orderId)
      );

      // Fetch updated orders
      await fetchAcceptedOrders();

      setConfirmDialog(false);
      setSelectedNotification(null);
      setDialogAction(null);

      // Show success message
      alert("Order accepted successfully!");
    } catch (error) {
      console.error("Failed to accept order:", error);
      alert(error.response?.data?.message || "Failed to accept order");
    } finally {
      setLoading(false);
    }
  };

  // ❌ Decline order
  const handleDeclineOrder = async () => {
    if (!selectedNotification) return;

    try {
      setLoading(true);
      await api.post(
        `/agent/notifications/${selectedNotification.orderId}/decline`
      );

      // Remove from notifications
      setNotifications((prev) =>
        prev.filter((n) => n.orderId !== selectedNotification.orderId)
      );

      setConfirmDialog(false);
      setSelectedNotification(null);
      setDialogAction(null);
    } catch (error) {
      console.error("Failed to decline order:", error);
      alert(error.response?.data?.message || "Failed to decline order");
    } finally {
      setLoading(false);
    }
  };

  // 🔄 Navigate to order details
  const goToOrderDetails = (orderId) => {
    navigate(`/agent/orders/${orderId}`);
  };

  // ⏱️ Format time remaining
  const getTimeRemaining = (expiresAt) => {
    if (!expiresAt) return "N/A";
    const now = new Date();
    const expires = new Date(expiresAt);
    if (Number.isNaN(expires.getTime())) return "N/A";
    const diff = expires.getTime() - now.getTime();

    if (diff <= 0) return "Expired";
    if (diff < 60000) return `${Math.floor(diff / 1000)}s`;
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
    return `${Math.floor(diff / 3600000)}h`;
  };

  const getNotificationVendorName = (notification) => {
    return (
      notification.vendorName ||
      notification.vendor?.name ||
      notification.vendor?.storeName ||
      notification.vendorCity ||
      "New Order"
    );
  };

  const getNotificationAmount = (notification) => {
    return notification.totalAmount ?? notification.amount ?? 0;
  };

  const getNotificationItemCount = (notification) => {
    return notification.itemCount ?? notification.items?.length ?? 0;
  };

  const getNotificationItems = (notification) => {
    return notification.items ?? [];
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
          Order Management
        </Typography>
        <Typography variant="body2" color="textSecondary">
          View notifications and manage your deliveries
        </Typography>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, val) => setTabValue(val)}>
          <Tab
            icon={<Badge badgeContent={notifications.length} color="error" />}
            label="Notifications"
            iconPosition="start"
          />
          <Tab
            icon={
              <Badge badgeContent={acceptedOrders.length} color="primary" />
            }
            label="Active Orders"
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* ====== NOTIFICATIONS TAB ====== */}
      {tabValue === 0 && (
        <Box>
          {notifications.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: "center" }}>
              <NotificationsActive
                sx={{ fontSize: 48, color: "textSecondary", mb: 2 }}
              />
              <Typography variant="h6" color="textSecondary">
                No new order notifications
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                New orders will appear here
              </Typography>
            </Paper>
          ) : (
            <Stack spacing={3}>
              {notifications.map((notification) => (
                <Card
                  key={notification.orderId}
                  sx={{
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    border: "2px solid #e3f2fd",
                    backgroundColor: notification.isExpired
                      ? "#fafafa"
                      : "#ffffff",
                  }}
                >
                  <CardContent>
                    {/* Header */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 2,
                      }}
                    >
                      <Box>
                        <Typography variant="subtitle1" fontWeight={700}>
                          {getNotificationVendorName(notification)}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {notification.vendorCity || "Location not available"}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: "right" }}>
                        <Typography
                          variant="h6"
                          fontWeight={700}
                          sx={{ color: "#2196f3" }}
                        >
                          ₹{getNotificationAmount(notification)}
                        </Typography>
                        <Chip
                          icon={<Timer />}
                          label={getTimeRemaining(notification.expiresAt)}
                          size="small"
                          color={notification.isExpired ? "default" : "warning"}
                          variant={notification.isExpired ? "outlined" : "filled"}
                          sx={{ mt: 1 }}
                        />
                      </Box>
                    </Box>

                    {/* Items */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="textSecondary">
                        Items ({getNotificationItemCount(notification)})
                      </Typography>
                      <Stack spacing={1} sx={{ mt: 1 }}>
                        {getNotificationItems(notification).map((item, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              fontSize: "0.85rem",
                            }}
                          >
                            <Typography variant="caption">
                              {item.title} x{item.qty}
                            </Typography>
                            <Typography variant="caption" fontWeight={600}>
                              ₹{item.price}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Box>

                    {/* Distance */}
                    <Box sx={{ mb: 3 }}>
                      <Chip
                        icon={<LocalShipping />}
                        label={`${notification.distanceKm} km away`}
                        variant="outlined"
                        size="small"
                      />
                    </Box>

                    {/* Actions */}
                    {!notification.isExpired ? (
                      <Stack direction="row" spacing={2}>
                        <Button
                          variant="contained"
                          color="success"
                          fullWidth
                          onClick={() => {
                            setSelectedNotification(notification);
                            setDialogAction("accept");
                            setConfirmDialog(true);
                          }}
                          disabled={loading}
                        >
                          Accept Delivery
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          fullWidth
                          onClick={() => {
                            setSelectedNotification(notification);
                            setDialogAction("decline");
                            setConfirmDialog(true);
                          }}
                          disabled={loading}
                        >
                          Decline
                        </Button>
                      </Stack>
                    ) : (
                      <Chip
                        label="Notification Expired"
                        color="error"
                        fullWidth
                      />
                    )}
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </Box>
      )}

      {/* ====== ACTIVE ORDERS TAB ====== */}
      {tabValue === 1 && (
        <Box>
          {acceptedOrders.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: "center" }}>
              <AssignmentTurnedIn
                sx={{ fontSize: 48, color: "textSecondary", mb: 2 }}
              />
              <Typography variant="h6" color="textSecondary">
                No active orders
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Accept orders from notifications to see them here
              </Typography>
            </Paper>
          ) : (
            <Stack spacing={3}>
              {acceptedOrders.map((order) => (
                <Card
                  key={order._id}
                  sx={{
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    "&:hover": {
                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    },
                  }}
                >
                  <CardContent>
                    {/* Header */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 2,
                      }}
                    >
                      <Box>
                        <Typography variant="subtitle1" fontWeight={700}>
                          Order #{order._id.substring(0, 8).toUpperCase()}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {new Date(order.createdAt).toLocaleDateString()}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: "right" }}>
                        <Typography
                          variant="h6"
                          fontWeight={700}
                          sx={{ color: "#2196f3" }}
                        >
                          ₹{order.total}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Status */}
                    <Box sx={{ mb: 2 }}>
                      <Chip
                        icon={<CheckCircle />}
                        label={order.status.replace(/_/g, " ")}
                        color="primary"
                        variant="outlined"
                        size="small"
                      />
                    </Box>

                    {/* 🟣 Vendor Phase Status */}
                    {order.vendorPhase?.status && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="textSecondary">
                          Vendor Status
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <Chip
                            label={`Vendor: ${order.vendorPhase.status}`}
                            size="small"
                            variant="outlined"
                            color={
                              order.vendorPhase.status === "READY_FOR_PICKUP"
                                ? "success"
                                : order.vendorPhase.status === "PREPARING"
                                ? "warning"
                                : "default"
                            }
                          />
                        </Box>
                      </Box>
                    )}

                    {/* Items */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="textSecondary">
                        Items ({order.items.length})
                      </Typography>
                      <Stack spacing={1} sx={{ mt: 1 }}>
                        {order.items.map((item, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              fontSize: "0.85rem",
                            }}
                          >
                            <Typography variant="caption">
                              {item.title} x{item.qty}
                            </Typography>
                            <Typography variant="caption" fontWeight={600}>
                              ₹{item.price}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Box>

                    {/* Distance & Address */}
                    <Box sx={{ mb: 2 }}>
                      {order.agent?.distanceKm && (
                        <Chip
                          icon={<LocalShipping />}
                          label={`${order.agent.distanceKm} km`}
                          variant="outlined"
                          size="small"
                          sx={{ mr: 1 }}
                        />
                      )}
                    </Box>

                    {/* Delivery Address */}
                    <Paper sx={{ p: 2, backgroundColor: "#f5f5f5", mb: 3 }}>
                      <Typography variant="caption" color="textSecondary">
                        Delivery Address
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {order.address.addressLine1}
                        {order.address.addressLine2 &&
                          `, ${order.address.addressLine2}`}
                      </Typography>
                      <Typography variant="caption">
                        {order.address.city}, {order.address.state}{" "}
                        {order.address.pincode}
                      </Typography>
                    </Paper>

                    {/* Tracking Timeline */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="caption" color="textSecondary">
                        Tracking
                      </Typography>
                      <Stack spacing={1} sx={{ mt: 1 }}>
                        {order.tracking &&
                          order.tracking.slice(-3).map((track, idx) => (
                            <Box key={idx} sx={{ display: "flex", gap: 1 }}>
                              <CheckCircle
                                sx={{
                                  fontSize: 16,
                                  color: track.completed
                                    ? "#4caf50"
                                    : "#ccc",
                                }}
                              />
                              <Box>
                                <Typography variant="caption">
                                  {track.label}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                >
                                  {new Date(track.time).toLocaleDateString()}
                                </Typography>
                              </Box>
                            </Box>
                          ))}
                      </Stack>
                    </Box>

                    {/* Action Button */}
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => goToOrderDetails(order._id)}
                    >
                      View & Update Status
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </Box>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
        <DialogTitle>
          {dialogAction === "accept" ? "Accept Order?" : "Decline Order?"}
        </DialogTitle>
        <DialogContent>
          {selectedNotification && (
            <Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {selectedNotification.vendorName}
              </Typography>
              <Typography variant="body2">
                ₹{selectedNotification.totalAmount}
              </Typography>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                {selectedNotification.distanceKm} km away
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialog(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          {dialogAction === "accept" ? (
            <Button
              onClick={handleAcceptOrder}
              variant="contained"
              color="success"
              disabled={loading}
            >
              {loading ? "Accepting..." : "Accept"}
            </Button>
          ) : (
            <Button
              onClick={handleDeclineOrder}
              variant="contained"
              color="error"
              disabled={loading}
            >
              {loading ? "Declining..." : "Decline"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AgentOrders;
